/** Per-machine contribution to welding price and cost. Used per spread in the
 *  welding breakdown and project-wide in the Defend step. */
import React from 'react';
import { CRC } from './moduleInputsData';
import { money } from './spreadSplitData';
import { MachineLine, machineMargin } from './weldingMachines';

const cell: React.CSSProperties = { fontSize: 12, color: CRC.textGrey, padding: '6px 0' };
const head: React.CSSProperties = {
  fontSize: 10, letterSpacing: '0.04em', textTransform: 'uppercase',
  color: CRC.heritageGrey, fontWeight: 600, paddingBottom: 6,
};
const pct = (n: number) => `${Math.round(n * 100)}%`;

const COLUMNS = ['Machine', 'Qty', 'Buy each', 'Rent/mo each', 'Price', 'Hardware cost', 'Margin'];

interface Props {
  rows: MachineLine[];
  /** Shown under the table to explain what the price column excludes. */
  footnote?: string;
}

export const MachineMixTable: React.FC<Props> = ({ rows, footnote }) => {
  if (rows.length === 0) {
    return <div style={{ fontSize: 12, color: CRC.heritageGrey }}>No machines allocated.</div>;
  }
  const totals = rows.reduce(
    (acc, r) => ({ units: acc.units + r.units, price: acc.price + r.totalPrice, cost: acc.cost + r.hardwareCost }),
    { units: 0, price: 0, cost: 0 }
  );
  const totalMargin = totals.price > 0 ? (totals.price - totals.cost) / totals.price : null;

  return (
    <>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {COLUMNS.map((h, i) => (
              <th key={h} style={{ ...head, textAlign: i === 0 ? 'left' : 'right' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const margin = machineMargin(row);
            const negative = margin !== null && margin < 0;
            return (
              <tr key={row.key} style={{ borderTop: `1px solid ${CRC.border}` }}>
                <td style={{ ...cell, fontWeight: 600, color: CRC.blueprintBlue }}>
                  {row.label}
                  {row.spareUnits > 0 && (
                    <span style={{ fontWeight: 400, color: CRC.heritageGrey }}> ({row.spareUnits} spare)</span>
                  )}
                </td>
                <td style={{ ...cell, textAlign: 'right' }}>{row.units}</td>
                <td style={{ ...cell, textAlign: 'right' }}>{money(row.unitCost)}</td>
                <td style={{ ...cell, textAlign: 'right' }}>{money(row.monthlyRentalPerUnit)}</td>
                <td style={{ ...cell, textAlign: 'right', fontWeight: 600 }}>{money(row.totalPrice)}</td>
                <td style={{ ...cell, textAlign: 'right' }}>{money(row.hardwareCost)}</td>
                <td style={{ ...cell, textAlign: 'right', fontWeight: 700, color: negative ? CRC.readableRed : CRC.blueprintBlue }}>
                  {margin === null ? '—' : pct(margin)}
                </td>
              </tr>
            );
          })}
          <tr style={{ borderTop: `2px solid ${CRC.border}` }}>
            <td style={{ ...cell, fontWeight: 700, color: CRC.blueprintBlue }}>Total</td>
            <td style={{ ...cell, textAlign: 'right', fontWeight: 700 }}>{totals.units}</td>
            <td style={cell} />
            <td style={cell} />
            <td style={{ ...cell, textAlign: 'right', fontWeight: 700, color: CRC.readableRed }}>{money(totals.price)}</td>
            <td style={{ ...cell, textAlign: 'right', fontWeight: 700 }}>{money(totals.cost)}</td>
            <td style={{ ...cell, textAlign: 'right', fontWeight: 700, color: (totalMargin ?? 0) < 0 ? CRC.readableRed : CRC.blueprintBlue }}>
              {totalMargin === null ? '—' : pct(totalMargin)}
            </td>
          </tr>
        </tbody>
      </table>
      <div style={{ fontSize: 10.5, color: CRC.heritageGrey, marginTop: 8 }}>
        {footnote ?? 'Price covers rental, software, connectivity and shipping for these machines. Hardware cost is the one-time purchase, so margin here compares rental revenue against buying the fleet.'}
      </div>
    </>
  );
};
